import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";

import { Section, SectionHeader } from "@/components/site/Section";
import { ThemeSummary } from "@/components/site/ThemeSummary";
import { getCourse } from "@/content/courses";
import { behaviorBySlug, behaviorTally, questionsFor } from "@/content/watch-party";
import { dimensions } from "@/content/behaviors";
import { getWatchParty, type WatchParty } from "@/utils/watch-party.functions";
import type { WatchPartySummary } from "@/content/watch-party";

export const Route = createFileRoute("/_authenticated/watch-party/$partyId/recap")({
  head: () => ({
    meta: [
      { title: "Watch party recap | Leadership Edge Live" },
      {
        name: "description",
        content: "What the room discussed, the behaviors it touched, and where to go next.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Watch party recap" },
      { property: "og:description", content: "Room-level themes from your watch party." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: WatchPartyRecap,
});

function WatchPartyRecap() {
  const { partyId } = Route.useParams();
  const load = useServerFn(getWatchParty);

  const [party, setParty] = useState<WatchParty | null>(null);
  const [summaries, setSummaries] = useState<WatchPartySummary[]>([]);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    load({ data: { partyId } })
      .then(({ party: p, summaries: s }) => {
        setParty(p);
        setSummaries(s);
      })
      .catch(() => setLoadError(true));
  }, [load, partyId]);

  const questions = useMemo(
    () => (party ? questionsFor(party.courseSlug, party.sessionIndex) : []),
    [party],
  );

  const tally = useMemo(
    () =>
      behaviorTally(
        questions,
        summaries.map((s) => s.questionIndex),
      ),
    [questions, summaries],
  );

  if (loadError) {
    return (
      <Section>
        <SectionHeader
          title="We couldn't find that watch party"
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
        />
        <Link
          to="/watch-party/"
          className="rounded-[6px] bg-[#693D94] px-6 py-4 text-base font-semibold text-primary-foreground shadow-brand"
        >
          Back to watch parties
        </Link>
      </Section>
    );
  }

  if (!party) {
    return (
      <Section>
        <p className="text-sm text-muted-foreground">Loading…</p>
      </Section>
    );
  }

  const course = getCourse(party.courseSlug);
  const topBehaviors = tally.slice(0, 3);

  return (
    <Section>
      <div className="mx-auto max-w-3xl">
        <SectionHeader
          eyebrow="Watch Party recap"
          title={course?.title ?? party.courseSlug}
          intro={`${party.attendeeCount} in the room · ${new Date(party.startedAt).toLocaleDateString()}`}
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
        />

        {questions.length === 0 ? (
          <p className="text-sm text-ink">
            Discussion questions coming soon for this course, so there's nothing to recap yet.
          </p>
        ) : summaries.length === 0 ? (
          <div className="rounded-2xl border border-border/60 bg-background p-6">
            <p className="text-sm text-ink">
              Nothing was captured in this watch party, so there are no themes to show. That's fine:
              the discussion still happened.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {summaries.map((summary) => (
              <div
                key={summary.questionIndex}
                className="rounded-2xl border border-border/60 bg-background p-6 md:p-7"
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Question {summary.questionIndex + 1}
                </p>
                <h3 className="mt-2 font-display text-xl font-semibold leading-snug text-ink">
                  {questions[summary.questionIndex]?.prompt}
                </h3>
                <div className="mt-5">
                  <ThemeSummary summary={summary} />
                </div>
              </div>
            ))}
          </div>
        )}

        {topBehaviors.length > 0 && (
          <div className="mt-10 rounded-2xl border border-border/60 bg-background p-6 md:p-7">
            <h3 className="font-display text-xl font-semibold text-ink">
              What the discussion touched
            </h3>
            <ul className="mt-5 space-y-5">
              {topBehaviors.map(({ slug, count }) => {
                const behavior = behaviorBySlug(slug);
                if (!behavior) return null;
                return (
                  <li key={slug}>
                    <p className="font-semibold text-ink">{behavior.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {dimensions[behavior.dimension].name} · came up in {count}{" "}
                      {count === 1 ? "question" : "questions"}
                    </p>
                    {behavior.curatedCourses.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {behavior.curatedCourses.slice(0, 3).map((c) => (
                          <li key={c.url}>
                            <a
                              href={c.url}
                              className="text-sm font-semibold text-primary underline"
                            >
                              {c.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link
            to="/watch-party/"
            className="rounded-[6px] bg-[#693D94] px-6 py-4 text-center text-base font-semibold text-primary-foreground shadow-brand"
          >
            Run another watch party
          </Link>
          <Link
            to="/course-catalog/"
            className="rounded-[6px] border border-border px-6 py-4 text-center text-base font-semibold text-ink"
          >
            Browse courses
          </Link>
        </div>
      </div>
    </Section>
  );
}
